import { User, UserManager } from "oidc-react";
import { WebStorageStateStore } from "oidc-client-ts";
import { getRuntimeConfig, getAuthConfig, isOpenIdProvider } from "../runtimeConfig";

let openidManager: UserManager | null = null;

/**
 * Get or create the OpenID UserManager instance with runtime configuration
 */
const getOpenIdManager = (): UserManager => {
  if (!openidManager) {
    let config;

    try {
      // Try to get runtime configuration
      if (isOpenIdProvider()) {
        config = getAuthConfig();
      } else {
        throw new Error("Not OpenID provider");
      }
    } catch {
      // Fallback to environment variables
      config = {
        authority: import.meta.env.VITE_OPENID_AUTHORITY ?? "",
        clientId: import.meta.env.VITE_OPENID_CLIENT_ID ?? "",
        scopes: import.meta.env.VITE_OPENID_SCOPES ?? "",
      };
    }

    openidManager = new UserManager({
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: window.location.origin,
      response_type: "code",
      scope: config.scopes,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: true,
      silent_redirect_uri: window.location.origin,
    });

    openidManager.events.addSilentRenewError((error) => {
      console.error("OpenID silent renew error:", error);

      // Handle token expiration silently in the background
      // Clear stored user data to force re-authentication on next request
      const manager = getOpenIdManager();
      manager.clearStaleState().catch(console.error);
      manager.removeUser().catch(console.error);

      // Clean up any redirect tracking
      localStorage.removeItem("openid-last-redirect");

      console.debug("Token expired - user will be prompted to re-authenticate on next request");
    });
  }

  return openidManager;
};

export const oidcConfig = {
  onBeforeSignIn: () => {
    const intitalref = window.location.href;
    window.localStorage.setItem("OpenIdInitialref", intitalref);
    return intitalref;
  },
  onSignIn: (user: User | null) => {
    const intitalref = window.localStorage.getItem("OpenIdInitialref");
    if (intitalref) window.location.href = intitalref;
  },
  userManager: getOpenIdManager(),
  location: window.location,
};

// Export the manager instance - this will be created on first access
const openid = getOpenIdManager();
export default openid;

export const getTokenAsync = async () => {
  const manager = getOpenIdManager();
  const user = await manager.getUser();
  return user?.access_token;
};

export const logout = async () => {
  const manager = getOpenIdManager();
  await manager.signoutRedirect();
};
